import { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { ModalContext } from '../ModalContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { Tab } from '@headlessui/react'
import moment from 'moment'

import KinesisEventManagementEditTableRow from 'components/Athlete/v2/KinesisEventManagementEditTableRow'
import KinesisEventManagementCreateTableRow from 'components/Athlete/v2/KinesisEventManagementCreateTableRow'

import OutlineButton from 'components/Buttons/OutlineButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

const tabs = [
    { key: 'past', text: 'Past' },
    { key: 'upcoming', text: 'Upcoming' },
]

const tableColumns = [
    {
        text: 'Priority',
        className: 'col-span-1'
    },
    {
        text: 'Name',
        className: 'col-span-2'
    },
    {
        text: 'Terrain',
        className: 'col-span-1'
    },
    {
        text: 'Custom Terrain',
        className: 'col-span-1'
    },
    {
        text: 'Distance',
        className: 'col-span-1'
    },
    {
        text: 'Custom Distance',
        className: 'col-span-1'
    },
    {
        text: 'Training Start Date',
        className: 'col-span-2'
    },
    {
        text: 'Event Date',
        className: 'col-span-2'
    },
    {
        text: 'Post Race Deload',
        className: 'col-span-1 text-center'
    },
    {
        text: 'Delete',
        className: 'col-span-1 text-center'
    }
]

const eventTypeOptions = [
    'a-race',
    'b-race',
    'c-race'
]

const eventTerrainOptions = [
    { key: 'road', title: 'Road' },
    { key: 'xterra', title: 'Xterra' },
    { key: 'gravel', title: 'Gravel' },
    { key: 'mtb', title: 'MTB' },
    { key: 'road-track', title: 'Road / Track' },
    { key: 'trail', title: 'Trail' },
    { key: 'short-course-meters', title: 'Short Course Meters' },
    { key: 'short-course-yards', title: 'Short Course Yards' },
    { key: 'long-course-meters', title: 'Long Course Meters' },
    { key: 'long-course-yards', title: 'Long Course Yards' },
    { key: 'other', title: 'Custom' }
]

const eventDistanceOptions = [
    { key: 'ironman', title: 'Ironman' },
    { key: 'half-ironman', title: 'Half Ironman' },
    { key: 'long-dist', title: 'Long Dist (hybrid dist)' },
    { key: 'olympic', title: 'Olympic' },
    { key: 'sprint-distance', title: 'Sprint Distance' },
    { key: 'super-sprint', title: 'Super Sprint' },
    { key: 'full-distance', title: 'Full Distance' },
    { key: 'short-course', title: 'Short Course' },
    { key: 'sport-distance', title: 'Sport distance' },
    { key: '5k', title: '5k' },
    { key: '10k', title: '10k' },
    { key: '20k', title: '20k' },
    { key: '40k', title: '40k' },
    { key: '50k', title: '50k' },
    { key: '100k', title: '100k' },
    { key: 'half-marathon', title: 'Half Marathon' },
    { key: 'marathon', title: 'Marathon' },
    { key: 'ultra-marathon', title: 'Ultra Marathon' },
    { key: '50-miles', title: '50 miles' },
    { key: '100-miles', title: '100 miles' },
    { key: '50-meters', title: '50 meters' },
    { key: '100-meters', title: '100 meters' },
    { key: '200-meters', title: '200 meters' },
    { key: '400-meters', title: '400 meters' },
    { key: '500-meters', title: '500 meters' },
    { key: '1500-meters', title: '1500 meters' },
    { key: 'other', title: 'Custom' }
]

export default function EventManagementModal() {

    const { modalData, HideModal } = useContext(ModalContext)

    const reduxGroupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)
    const trainingChartPresentDate = useSelector(state => state.athleteProfile.trainingChartPresentDate)
    const reduxKinesisEvents = useSelector(state => state.athleteProfile.kinesisEvents)

    const groupId = reduxGroupId ?? modalData.groupId ?? null
    const kinesisEvents = modalData.modalKinesisEvents ?? reduxKinesisEvents ?? []

    const [selectedTabKey, setSelectedTabKey] = useState('upcoming')
    const [editedKinesisEvents, setEditedKinesisEvents] = useState(() => JSON.parse(JSON.stringify(kinesisEvents)))
    const [newKinesisEvents, setNewKinesisEvents] = useState([])
    const [formErrorMessages, setFormErrorMessages] = useState([])
    const [backendSyncLoading, setBackendSyncLoading] = useState(false)

    const todayStart = moment().startOf('day')
    const pastEditableEvents = editedKinesisEvents.filter((event) => moment(event.eventDate, 'YYYY-MM-DD', true).isBefore(todayStart, 'day'))
    const upcomingEditableEvents = editedKinesisEvents.filter((event) => !moment(event.eventDate, 'YYYY-MM-DD', true).isBefore(todayStart, 'day'))

    const GetOriginalEvent = (editableKinesisEventId) => {
        return kinesisEvents.find(event => event.id === editableKinesisEventId)
    }

    const GetEventUpdateDataForAPICall = () => {

        var eventsToUpdate = []

        for (const event of editedKinesisEvents) {

            var originalEvent = GetOriginalEvent(event.id)

            if (JSON.stringify(originalEvent) !== JSON.stringify(event)) {
                eventsToUpdate.push(event)
            }
        }

        return eventsToUpdate

    }

    const ValidateEventData = (eventData) => {

        var errorMessages = []

        // Validate event name first

        if (eventData.name.length === 0) {
            errorMessages.push('An event is missing a name')
            return errorMessages
        }

        // Validate eventType

        if (['a-race', 'b-race', 'c-race'].includes(eventData.eventType) === false) {
            errorMessages.push("\"" + eventData.name + "\" has an invalid event type")
        }

        // Validate eventTerrain

        if (eventData.eventTerrain.length === 0) {
            errorMessages.push("\"" + eventData.name + "\" is missing terrain")
        }

        if (eventData.eventTerrain === 'other' && eventData.eventTerrainTextInput.length === 0) {
            errorMessages.push("\"" + eventData.name + "\" is missing custom terrain details")
        }

        // Validate eventDistance

        if (eventData.eventDistance.length === 0) {
            errorMessages.push("\"" + eventData.name + "\" is missing distance")
        }

        if (eventData.eventDistance === 'other' && eventData.eventDistanceTextInput.length === 0) {
            errorMessages.push("\"" + eventData.name + "\" is missing custom distance details")
        }

        // Validate eventDate

        if (moment(eventData.eventDate, 'YYYY-MM-DD', true).isValid() === false) {
            errorMessages.push("\"" + eventData.name + "\" is missing a valid event date")
        }

        // Validate a-race program date range

        if (eventData.eventType === 'a-race') {

            // Validate event training start date

            if (moment(eventData.eventTrainingStartDate, 'YYYY-MM-DD', true).isValid() === false) {
                errorMessages.push("\"" + eventData.name + "\" is missing a valid training start date")
            }

            // Validate event training start date is before event date

            if (moment(eventData.eventTrainingStartDate).isAfter(moment(eventData.eventDate))) {
                errorMessages.push("\"" + eventData.name + "\" training start date must be before event date")
            }

            // Validate event training start date is not on the same ISO week as event date

            if (moment(eventData.eventTrainingStartDate).isoWeek() === moment(eventData.eventDate).isoWeek()) {
                errorMessages.push("\"" + eventData.name + "\" training start date and event date must be on different weeks")
            }

            // Validate event training start date and event date are within 360 days of each other

            if (moment(eventData.eventDate).diff(moment(eventData.eventTrainingStartDate), 'days') > 360) {
                errorMessages.push("\"" + eventData.name + "\" training start date and event date must be within 360 days of each other")
            }
        }

        return errorMessages
    }

    const HandleEditedEventDataEdit = (kinesisEventId, propertyName, value) => {
        setEditedKinesisEvents(prev => prev.map(event =>
            event.id === kinesisEventId ? { ...event, [propertyName]: value } : event
        ))
    }

    const HandleNewEventDataEdit = (newKinesisEventIndex, propertyName, value) => {
        if (propertyName === 'delete') {
            setNewKinesisEvents(prev => prev.filter((_, index) => index !== newKinesisEventIndex))
            return
        }

        setNewKinesisEvents(prev => prev.map((event, index) =>
            index === newKinesisEventIndex ? { ...event, [propertyName]: value } : event
        ))
    }

    const OnCreateNewEvent = () => {

        const newKinesisEvent = {
            eventType: "a-race",
            name: "New Event",
            eventTerrain: "road",
            eventTerrainTextInput: "",
            eventDistance: "5k",
            eventDistanceTextInput: "",
            eventTrainingStartDate: moment().utc().local().format('YYYY-MM-DD'),
            eventDate: moment().add(16, 'weeks').utc().local().format('YYYY-MM-DD'),
            deloadAfterEvent: 1,
            archived: 0
        }

        setNewKinesisEvents(prev => [...prev, newKinesisEvent])
    }

    const SyncEventManagementChangesToBackend = async () => {

        setBackendSyncLoading(true)

        setFormErrorMessages([])

        // Get event data

        var eventsToUpdate = GetEventUpdateDataForAPICall()

        var eventsToInsert = JSON.parse(JSON.stringify(newKinesisEvents))

        // Validate event data

        var newFormErrorMessages = []

        for (const event of eventsToUpdate) {
            const errorMessages = ValidateEventData(event)
            if (errorMessages.length > 0) newFormErrorMessages.push(...errorMessages)
        }

        for (const event of eventsToInsert) {
            const errorMessages = ValidateEventData(event)
            if (errorMessages.length > 0) newFormErrorMessages.push(...errorMessages)
        }

        if (newFormErrorMessages.length > 0) {
            setFormErrorMessages(newFormErrorMessages)
            setBackendSyncLoading(false)
            return
        }

        // Save event data to backend

        if (eventsToUpdate.length > 0 || eventsToInsert.length > 0) {

            if (modalData.profileType === 'group') {
                const jsonBody = {
                    groupId: groupId,
                    localDate: trainingChartPresentDate,
                    eventsToUpdate: eventsToUpdate,
                    eventsToInsert: eventsToInsert,
                }

                // console.log(jsonBody)

                const result = await WhichWeightAPI('PROGRAM', 'POST', '/training_chart_group_event_management_sync', jsonBody)

                // console.log(result)

                if (result.data.status === 'failure') {
                    console.error(result)
                    setFormErrorMessages([result.data.message])
                    setBackendSyncLoading(false)
                    return
                }
            }

            if (modalData.profileType === 'individual') {
                const jsonBody = {
                    userId: athleteId,
                    localDate: trainingChartPresentDate,
                    eventsToUpdate: eventsToUpdate,
                    eventsToInsert: eventsToInsert,
                }

                // console.log(jsonBody)

                const result = await WhichWeightAPI('PROGRAM', 'POST', '/training_chart_event_management_sync', jsonBody)

                // console.log(result)

                if (result.data.status === 'failure') {
                    console.error(result)
                    setFormErrorMessages([result.data.message])
                    setBackendSyncLoading(false)
                    return
                }
            }

        }

        setBackendSyncLoading(false)

        modalData.ReloadTrainingChartData()

        HideModal()
    }

    useEffect(() => { setFormErrorMessages([]) }, [editedKinesisEvents, newKinesisEvents])

    return (
        <div className="flex flex-col h-full items-center">
            <div className="w-1/2 pb-2 text-xl font-semibold leading-6">
                Manage Races and Events
            </div>

            <Tab.Group>
                <Tab.List className="w-full flex border-b border-gray-300">
                    {tabs.map((tab, index) => (
                        <Tab
                            key={index}
                            className={`
                                flex-1 px-6 py-3 text-sm font-medium leading-5 outline-none
                                ${selectedTabKey !== tab.key ? 'text-gray-500 hover:text-gray-700 hover:border-gray-300' : ''}
                                ${selectedTabKey === tab.key ? 'text-orange-600 border-b-2 border-orange-500' : ''}
                            `}
                            onClick={() => setSelectedTabKey(tab.key)}
                        >
                            {tab.text}
                        </Tab>
                    ))}
                </Tab.List>
            </Tab.Group>

            {selectedTabKey === 'past' && (
                <div className="flex-grow w-full py-4 space-y-4 text-left">
                    {pastEditableEvents.length === 0 &&
                        <div className='p-2 text-center text-gray-500 bg-gray-100 rounded-lg'>No events found</div>
                    }

                    {pastEditableEvents.length !== 0 &&
                        <div
                            className={`
                                border border-gray-400 rounded-lg overflow-hidden
                                divide-y divide-gray-400
                                text-sm
                            `}
                        >
                            <div className='grid grid-cols-13 bg-gray-100 font-bold divide-x divide-gray-400'>
                                {tableColumns.map((column, columnIndex) => (
                                    <div
                                        key={columnIndex}
                                        className={`p-2 text-xs truncate ${column.className}`}
                                    >
                                        {column.text}
                                    </div>
                                ))}
                            </div>

                            {pastEditableEvents.map((pastEditableEvent) => (
                                <KinesisEventManagementEditTableRow
                                    key={pastEditableEvent.id}
                                    originalKinesisEvent={GetOriginalEvent(pastEditableEvent.id)}
                                    editableKinesisEvent={pastEditableEvent}
                                    eventTypeOptions={eventTypeOptions}
                                    eventTerrainOptions={eventTerrainOptions}
                                    eventDistanceOptions={eventDistanceOptions}
                                    onEventDataEdit={HandleEditedEventDataEdit}
                                />
                            ))}
                        </div>
                    }
                </div>
            )}

            {selectedTabKey === 'upcoming' && (
                <div className="flex-grow w-full py-4 space-y-4 text-left">
                    {(
                        upcomingEditableEvents.length === 0 
                        && newKinesisEvents.length === 0
                    ) &&
                        <div className='p-2 text-center text-gray-500 bg-gray-100 rounded-lg'>No events found</div>
                    }

                    {upcomingEditableEvents.length > 0 &&
                        <div
                            className={`
                                border border-gray-400 rounded-lg overflow-hidden
                                divide-y divide-gray-400
                                text-sm
                            `}
                        >
                            <div className='grid grid-cols-13 bg-gray-100 font-bold divide-x divide-gray-400'>
                                {tableColumns.map((column, columnIndex) => (
                                    <div
                                        key={columnIndex}
                                        className={`p-2 text-xs truncate ${column.className}`}
                                    >
                                        {column.text}
                                    </div>
                                ))}
                            </div>

                            {upcomingEditableEvents.map((upcomingEditableEvent) => (
                                <KinesisEventManagementEditTableRow
                                    key={upcomingEditableEvent.id}
                                    originalKinesisEvent={GetOriginalEvent(upcomingEditableEvent.id)}
                                    editableKinesisEvent={upcomingEditableEvent}
                                    eventTypeOptions={eventTypeOptions}
                                    eventTerrainOptions={eventTerrainOptions}
                                    eventDistanceOptions={eventDistanceOptions}
                                    onEventDataEdit={HandleEditedEventDataEdit}
                                />
                            ))}
                        </div>
                    }

                    {newKinesisEvents.length > 0 &&
                        <div
                            className={`
                                border border-gray-400 rounded-lg overflow-hidden
                                divide-y divide-gray-400
                                text-sm
                            `}
                        >
                            <div className='grid grid-cols-13 bg-gray-100 font-bold divide-x divide-gray-400'>
                                {tableColumns.map((column, columnIndex) => (
                                    <div 
                                    key={columnIndex} 
                                    className={`p-2 text-xs truncate ${column.className}`}
                                    >
                                        {column.text}
                                    </div>
                                ))}
                            </div>

                            {newKinesisEvents.map((newKinesisEvent, newKinesisEventIndex) => (
                                <KinesisEventManagementCreateTableRow 
                                    key={newKinesisEventIndex}
                                    newKinesisEventIndex={newKinesisEventIndex}
                                    newKinesisEvent={newKinesisEvent}
                                    eventTypeOptions={eventTypeOptions}
                                    eventTerrainOptions={eventTerrainOptions}
                                    eventDistanceOptions={eventDistanceOptions}
                                    onNewEventDataEdit={HandleNewEventDataEdit}
                                />
                            ))}
                        </div>
                    }

                    <div 
                        className='p-2 text-center cursor-pointer hover:text-gray-600 select-none'
                        onClick={OnCreateNewEvent}
                    >
                        Create New Event
                    </div>
                </div>
            )}

            <div className='mt-auto w-full space-y-4'>
                {formErrorMessages.length > 0 && formErrorMessages.map((errorMessage, errorMessageIndex) => (
                    <div
                        key={errorMessageIndex}
                        className='p-2 text-center text-red-600 rounded-xl'
                    >
                        {errorMessage}
                    </div>
                ))}

                <div className='flex items-center gap-x-4'>
                    <div className='flex-1'>
                        <OutlineButton
                            size="large"
                            text="Cancel"
                            action={HideModal}
                        />
                    </div>

                    <div className='flex-1'>
                        <BrandColorButton
                            size="large"
                            text="Save Changes"
                            action={SyncEventManagementChangesToBackend}
                            isLoading={backendSyncLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
